import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook to handle mobile viewport and keyboard detection
 * Uses the visualViewport API for accurate keyboard height detection
 *
 * Jul 2026 (Stability Phase 5): the CSS variables are now initialized
 * PRE-PAINT by an inline script in public/index.html, so this hook's
 * first write is identical to what's already applied — no post-load
 * layout shift. Additionally, writes carry hysteresis: browser-chrome
 * drift (URL bar show/hide, sub-24px deltas) no longer rewrites the
 * variables; only real changes (keyboard, rotation) do.
 */
const SHIFT_THRESHOLD_PX = 24;

export const useMobileViewport = () => {
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(
    typeof window !== 'undefined' ? window.innerHeight : 0
  );
  // Last values actually written to CSS — hysteresis reference.
  const lastWrittenRef = useRef({ h: null, off: null, kb: 0 });

  // Check if we're on mobile
  const isMobile = typeof window !== 'undefined' && 
    /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const updateViewport = useCallback(() => {
    if (typeof window === 'undefined') return;

    const visualViewport = window.visualViewport;
    const root = document.documentElement;
    const layoutHeight = Math.max(window.innerHeight, root.clientHeight || 0);
    
    if (visualViewport) {
      // Use visualViewport API for accurate measurements
      const currentHeight = visualViewport.height;
      const viewportOffsetBottom = Math.max(0, layoutHeight - (visualViewport.height + visualViewport.offsetTop));
      const keyboardH = Math.max(0, layoutHeight - currentHeight);
      
      setViewportHeight(currentHeight);
      setKeyboardHeight(keyboardH);
      setIsKeyboardOpen(keyboardH > 100); // Threshold to avoid false positives

      // Hysteresis: skip CSS writes for browser-chrome drift. Only
      // rewrite when the change is real — a large height delta
      // (rotation) or a keyboard open/close transition. This prevents
      // the layout from twitching as mobile URL bars expand/collapse.
      const last = lastWrittenRef.current;
      const keyboardChanged = (last.kb > 100) !== (keyboardH > 100);
      const bigShift =
        last.h === null ||
        Math.abs(currentHeight - last.h) >= SHIFT_THRESHOLD_PX ||
        Math.abs(viewportOffsetBottom - (last.off ?? 0)) >= SHIFT_THRESHOLD_PX;
      if (!keyboardChanged && !bigShift) return;
      lastWrittenRef.current = { h: currentHeight, off: viewportOffsetBottom, kb: keyboardH };

      // Update CSS custom property for viewport height
      root.style.setProperty(
        '--viewport-height',
        `${currentHeight}px`
      );
      root.style.setProperty(
        '--keyboard-height',
        `${keyboardH}px`
      );
      root.style.setProperty(
        '--viewport-offset-bottom',
        `${viewportOffsetBottom}px`
      );
    } else {
      // Fallback for browsers without visualViewport
      setViewportHeight(layoutHeight);
      root.style.setProperty(
        '--viewport-height',
        `${layoutHeight}px`
      );
      root.style.setProperty(
        '--keyboard-height',
        '0px'
      );
      root.style.setProperty(
        '--viewport-offset-bottom',
        '0px'
      );
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Set initial CSS variables (do this first, before state updates)
    const initialHeight = window.visualViewport?.height || Math.max(window.innerHeight, document.documentElement.clientHeight || 0);
    const initialOffset = window.visualViewport
      ? Math.max(0, Math.max(window.innerHeight, document.documentElement.clientHeight || 0) - (window.visualViewport.height + window.visualViewport.offsetTop))
      : 0;
    document.documentElement.style.setProperty(
      '--viewport-height',
      `${initialHeight}px`
    );
    document.documentElement.style.setProperty(
      '--keyboard-height',
      '0px'
    );
    document.documentElement.style.setProperty(
      '--viewport-offset-bottom',
      `${initialOffset}px`
    );

    // Use a microtask to update state after initial render
    queueMicrotask(() => {
      updateViewport();
    });

    const visualViewport = window.visualViewport;

    if (visualViewport) {
      // Listen to visualViewport events
      visualViewport.addEventListener('resize', updateViewport);
      visualViewport.addEventListener('scroll', updateViewport);
    }

    // Also listen to resize and orientation change
    window.addEventListener('resize', updateViewport);
    window.addEventListener('orientationchange', updateViewport);

    // Focus/blur events to detect keyboard
    const handleFocus = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        // Small delay to let keyboard open
        setTimeout(updateViewport, 100);
        setTimeout(updateViewport, 300);
        setTimeout(() => {
          e.target.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
        }, 220);
      }
    };

    const handleBlur = () => {
      setTimeout(updateViewport, 100);
    };

    document.addEventListener('focusin', handleFocus);
    document.addEventListener('focusout', handleBlur);
    window.addEventListener('pageshow', updateViewport);

    return () => {
      if (visualViewport) {
        visualViewport.removeEventListener('resize', updateViewport);
        visualViewport.removeEventListener('scroll', updateViewport);
      }
      window.removeEventListener('resize', updateViewport);
      window.removeEventListener('orientationchange', updateViewport);
      window.removeEventListener('pageshow', updateViewport);
      document.removeEventListener('focusin', handleFocus);
      document.removeEventListener('focusout', handleBlur);
    };
  }, [updateViewport]);

  return {
    keyboardHeight,
    isKeyboardOpen,
    viewportHeight,
    isMobile,
  };
};

export default useMobileViewport;
